import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { getAuthHeaders } from '../utils/auth';
import {
  Clock, Plus, Play, Trash2, Wallet, Target, Lightbulb, Repeat,
  Sparkles, Send, Loader2, Bell,
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { PushToggle } from '../components/PushToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { wallet: Wallet, target: Target, lightbulb: Lightbulb, repeat: Repeat, sparkles: Sparkles, clock: Clock };
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const scheduleLabel = (t) => {
  const h = t.hour_local ?? 9;
  const hh = h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`;
  if (t.cadence === 'hourly') return 'Every hour';
  if (t.cadence === 'weekly') return `${WEEKDAYS[t.weekday ?? 0]}s at ${hh}`;
  return `Daily at ${hh}`;
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const timeUntil = (iso) => {
  if (!iso) return '';
  const s = (new Date(iso) - Date.now()) / 1000;
  if (s <= 60) return 'any moment';
  if (s < 3600) return `in ${Math.ceil(s / 60)}m`;
  if (s < 86400) return `in ${Math.round(s / 3600)}h`;
  return `in ${Math.round(s / 86400)}d`;
};

const FEED_ICONS = { wealth_pulse: Wallet, opportunity_scan: Target, invention_nudge: Lightbulb, recurring_watch: Repeat, custom: Sparkles };

const TaskIcon = ({ icon }) => {
  const I = ICONS[icon] || Clock;
  return (
    <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
      <I className="w-4 h-4 text-amber-400" />
    </div>
  );
};

export default function ScheduledTasks() {
  const [tasks, setTasks] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [runs, setRuns] = useState([]);
  const [tab, setTab] = useState('tasks');
  const [input, setInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const inputRef = useRef(null);

  const load = () => {
    axios.get(`${API}/tasks`, { headers: getAuthHeaders() })
      .then((r) => { setTasks(r.data.tasks || []); setSuggestions(r.data.suggestions || []); })
      .catch(() => {});
    axios.get(`${API}/tasks/feed`, { headers: getAuthHeaders() })
      .then((r) => setRuns(r.data.runs || [])).catch(() => {});
  };
  useEffect(load, []);

  const addPreset = async (preset) => {
    try {
      await axios.post(`${API}/tasks`, { preset }, { headers: getAuthHeaders() });
      toast.success('Task scheduled — LEE will run it automatically');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add task'); }
  };

  const createCustom = async () => {
    if (!input.trim() || creating) return;
    setCreating(true);
    try {
      const r = await axios.post(`${API}/tasks`, { instruction: input.trim() }, { headers: getAuthHeaders() });
      toast.success(`Scheduled: ${r.data.task.title} · ${scheduleLabel(r.data.task)}`);
      setInput('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not schedule task'); }
    setCreating(false);
  };

  const toggle = async (t) => {
    setTasks((p) => p.map((x) => (x.id === t.id ? { ...x, enabled: !t.enabled } : x)));
    try {
      await axios.patch(`${API}/tasks/${t.id}`, { enabled: !t.enabled }, { headers: getAuthHeaders() });
    } catch (e) { toast.error('Could not update'); load(); }
  };

  const runNow = async (t) => {
    setRunningId(t.id);
    try {
      const r = await axios.post(`${API}/tasks/${t.id}/run`, {}, { headers: getAuthHeaders() });
      toast.success(r.data.run?.title || 'Task ran');
      load();
      setTab('feed');
    } catch (e) { toast.error('Run failed'); }
    setRunningId(null);
  };

  const remove = async (t) => {
    try {
      await axios.delete(`${API}/tasks/${t.id}`, { headers: getAuthHeaders() });
      toast.success('Task removed');
      load();
    } catch (e) { toast.error('Could not remove'); }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto pb-28" data-testid="scheduled-tasks-page">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2.5">
            <Clock className="w-6 h-6 text-amber-400" /> Scheduled
          </h1>
          <div className="flex rounded-full bg-white/[0.05] p-0.5">
            {[['tasks', 'Tasks'], ['feed', 'Results']].map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)}
                className={`px-4 py-1.5 rounded-full text-[12px] font-bold transition-colors ${tab === k ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-zinc-200'}`}
                data-testid={`tab-${k}`}>
                {label}{k === 'feed' && runs.length > 0 ? ` (${runs.length})` : ''}
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-zinc-500 mb-4">LEE runs these automatically and pushes the results to your phone.</p>

        <div className="mb-5"><PushToggle /></div>

        {tab === 'tasks' && (
          <div className="space-y-3">
            {tasks.map((t) => (
              <div key={t.id}
                className={`rounded-2xl border p-4 transition-opacity ${t.enabled ? 'border-white/10 bg-white/[0.03]' : 'border-white/[0.05] bg-transparent opacity-55'}`}
                data-testid="task-card">
                <div className="flex items-start gap-3">
                  <TaskIcon icon={t.icon} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[15px] font-semibold text-zinc-100 truncate">{t.title}</p>
                      <Switch checked={!!t.enabled} onCheckedChange={() => toggle(t)} data-testid="task-toggle" />
                    </div>
                    <p className="text-[13px] text-zinc-500 mt-0.5 leading-snug">{t.description}</p>
                    <div className="flex items-center justify-between gap-2 mt-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[11px] font-mono text-amber-400/90 bg-amber-500/[0.08] px-2 py-0.5 rounded-full whitespace-nowrap">{scheduleLabel(t)}</span>
                        {t.enabled && t.next_run_at && (
                          <span className="text-[11px] text-zinc-600 truncate" data-testid="task-next-run">next {timeUntil(t.next_run_at)}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <button onClick={() => runNow(t)} disabled={runningId === t.id}
                          className="flex items-center gap-1 text-[11px] font-bold text-emerald-400 hover:text-emerald-300 transition-colors disabled:opacity-50"
                          data-testid="task-run-now-btn">
                          {runningId === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Run now
                        </button>
                        <button onClick={() => remove(t)} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid="task-delete-btn">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {t.last_run_at && <p className="text-[11px] text-zinc-600 mt-1.5">last ran {timeAgo(t.last_run_at)}</p>}
                  </div>
                </div>
              </div>
            ))}
            {tasks.length === 0 && (
              <p className="text-sm text-zinc-500 py-4 text-center">No scheduled tasks yet — add one below.</p>
            )}

            {suggestions.length > 0 && (
              <>
                <p className="text-[11px] uppercase tracking-widest text-zinc-600 font-bold pt-4">Suggested</p>
                {suggestions.map((s) => (
                  <button key={s.preset} onClick={() => addPreset(s.preset)}
                    className="w-full text-left rounded-2xl border border-dashed border-white/15 p-4 hover:border-amber-500/40 hover:bg-white/[0.02] transition-colors group"
                    data-testid={`suggestion-${s.preset}`}>
                    <div className="flex items-start gap-3">
                      <TaskIcon icon={s.icon} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[15px] font-semibold text-zinc-200 truncate">{s.title}</p>
                          <Plus className="w-4 h-4 text-zinc-500 group-hover:text-amber-400 transition-colors shrink-0" />
                        </div>
                        <p className="text-[13px] text-zinc-500 mt-0.5 leading-snug">{s.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        )}

        {tab === 'feed' && (
          <div className="space-y-3" data-testid="task-feed">
            {runs.length === 0 && (
              <div className="text-center py-10">
                <Bell className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">Results will land here when tasks run.</p>
              </div>
            )}
            {runs.map((r) => {
              const FIcon = FEED_ICONS[r.task_type] || Clock;
              return (
                <div key={r.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="feed-item">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[14px] font-semibold text-zinc-100 flex items-center gap-2 min-w-0">
                      <FIcon className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                      <span className="truncate">{r.title}</span>
                    </p>
                    <span className="text-[11px] text-zinc-600 shrink-0">{timeAgo(r.ran_at)}</span>
                  </div>
                  <p className="text-[13px] text-zinc-400 mt-1.5 leading-relaxed whitespace-pre-wrap">{r.body}</p>
                  {r.status === 'error' && <span className="text-[11px] text-red-400 font-bold">run failed</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Composer — schedule anything in plain English */}
        <div className="fixed bottom-16 md:bottom-4 left-0 right-0 md:left-20 z-40 px-4">
          <div className="max-w-2xl mx-auto flex items-center gap-2 rounded-full border border-white/15 bg-[#141416]/95 backdrop-blur-xl px-4 py-2 shadow-2xl">
            <Plus className="w-4 h-4 text-zinc-500 shrink-0" />
            <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createCustom()}
              placeholder="Schedule a task… e.g. every Friday summarize my spending"
              className="flex-1 bg-transparent text-base sm:text-sm text-white placeholder-zinc-600 outline-none min-w-0"
              data-testid="task-composer-input" />
            <button onClick={createCustom} disabled={creating || !input.trim()}
              className="w-8 h-8 rounded-full bg-amber-500 text-black flex items-center justify-center disabled:opacity-40 transition-opacity shrink-0"
              data-testid="task-composer-send">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
